import {Label, Spinner, Textarea, Tooltip} from "flowbite-react";
import {HiCheckCircle} from "react-icons/hi";
import CustomButton from "../../../components/common/CustomButton";
import {ConfirmModal, Drawer} from "../../../components/Modals";
import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useProjectService} from "../../../services";
import {notify} from 'react-notify-toast';

interface SyncModalProps {
  showSyncModal: boolean;
  setShowSyncModal: (showSyncModal: boolean) => void;
}

const SyncModal: React.FC<SyncModalProps> = ({ showSyncModal, setShowSyncModal }) => {
  const [selectedPMtool, setSelectedPMtool] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState<any>(null);
  const [statusStages, setStatusStages] = useState('');
  const [showSyncTicketConfirm, setShowSyncTicketConfirm] = useState(false);

  const {
    syncWithJira,
    syncWithAsana,
    syncWithAzure,
    syncWithMonday,
    updateProjectStatusStages,
  } = useProjectService();

  const {
    currentEntityId,
  } = useSelector((state: any) => state.timeline);

  const {
    projectIntegrationsLoading,
    projectIntegrations,
    syncWithJiraLoading,
  } = useSelector((state: any) => state.projects);


  useEffect(() => {
    if (projectIntegrations && projectIntegrations.length) {
      const _filterIntegrations = projectIntegrations.filter((integrations: any) => integrations.isSelected);
      if (_filterIntegrations.length) {
        const [integrations = {}] = _filterIntegrations;
        setSelectedPMtool(integrations.type);
      }
    }
  }, [projectIntegrations]);

  useEffect(() => {
    if (selectedPMtool && projectIntegrations) {
      const selectedIntegration = projectIntegrations.find((int: any) => int.type === selectedPMtool);
      if (selectedIntegration?.status_stages) {
        setStatusStages(JSON.stringify(selectedIntegration.status_stages, null, 4));
      }
    }
  }, [selectedPMtool, projectIntegrations]);

  useEffect(() => {
    if (projectIntegrations && projectIntegrations.length && selectedPMtool) {
      const foundIntegration = projectIntegrations.find((int: any) => int.type === selectedPMtool);
      setSelectedIntegration(foundIntegration);
    }
  }, [projectIntegrations, selectedPMtool]);

  // Logo mapping for integrations
  const logoMap = {
    jira: "jiralogo",
    asana: "asana",
    azure: "devopsAsure",
    monday: "mondaylogo",
  };

  const handleSaveSwimLanes = async () => {
    try {
      if (currentEntityId) {
        await updateProjectStatusStages(currentEntityId, statusStages);
        notify.show("Swimlanes updated successfully!", "success", 2000);
      }
    } catch (error) {
      notify.show("Failed to update swimlanes", "error", 2000);
    }
  };

  const handleSubmitSyncClick = async () => {
    setShowSyncTicketConfirm(true);
  };

  const handleSyncTicketConfirm = () => {
    setShowSyncTicketConfirm(!showSyncTicketConfirm);
  };

  const handleCloseSyncModal = () => {
    if (!syncWithJiraLoading) {
      setShowSyncModal(false);
      setSelectedPMtool('');
      setStatusStages('');
    }
  };

  const submitSyncTicket = async () => {
    if (!syncWithJiraLoading && currentEntityId) {
      setShowSyncTicketConfirm(!showSyncTicketConfirm);
      if (selectedPMtool === "jira") {
        await syncWithJira(currentEntityId);
        handleCloseSyncModal();
      } else if (selectedPMtool === "asana") {
        await syncWithAsana(currentEntityId);
        handleCloseSyncModal();
      } else if (selectedPMtool === "azure") {
        await syncWithAzure(currentEntityId);
        handleCloseSyncModal();
      } else if (selectedPMtool === "monday") {
        await syncWithMonday(currentEntityId);
        handleCloseSyncModal();
      }
      setStatusStages('');
    }
  };

  const handleSwimlaneChange = (e: any) => {
    setStatusStages(e.target.value);
  };

  const handleIntegrationClick = (type: any) => {
    if (projectIntegrations && projectIntegrations.length) {
      const _filterIntegrations = projectIntegrations.filter((integrations: any) => integrations.isSelected);
      const foundIntegration = projectIntegrations.find((int: any) => int.type === type);
      setSelectedIntegration(foundIntegration);

      if (_filterIntegrations.length) {
        const [integrations = {}] = _filterIntegrations;
        if (integrations.type === type) {
          setSelectedPMtool(type);
          if (foundIntegration?.status_stages) {
            setStatusStages(JSON.stringify(foundIntegration.status_stages, null, 4));
          }
        }
      } else {
        setSelectedPMtool(type);
        if (foundIntegration?.status_stages) {
          setStatusStages(JSON.stringify(foundIntegration.status_stages, null, 4));
        }
      }
    }
  };

  return (
    <>

      <Drawer
        isOpen={showSyncModal && !showSyncTicketConfirm}
        onClose={handleCloseSyncModal}
        position="right"
      >
        <div className="relative h-[calc(100%-55px)] overflow-auto flex flex-col">
          <div className={`flex flex-row border-b border-gray-200 !p-6 dark:border-gray-700 bg-primary-dark text-white sticky top-0 z-40`}>
            <div className="flex w-full h-12 mr-10 items-center">
              <div className="text-center dark:text-white text-white text-sm font-normal font-['Inter'] leading-3">Sync Tickets</div>
              <button
                type="button"
                className="ml-auto absolute right-[10px] top-0 dark:text-white text-white w-6 h-6 rounded-full bg-black text-sm"
                onClick={handleCloseSyncModal}
              >
                X
              </button>
            </div>
          </div>
          <div className='p-5'>
            {(projectIntegrationsLoading || syncWithJiraLoading) ? (
              <>
                {projectIntegrationsLoading && (
                  <div className="text-center w-full h-full bottom-0">
                    <Spinner aria-label="Center-aligned spinner example" />
                  </div>
                )}
                {syncWithJiraLoading && (
                  <div className="text-center dark:text-white text-gray-700 text-md font-normal font-['Inter']">
                    Syncing tickets in the PM tool, please wait.
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col gap-5 dark:text-white text-gray-700">
                <div className="flex gap-5">
                  {(projectIntegrations && projectIntegrations.length && projectIntegrations.filter((int: any) => int.isActive).length) ?
                    projectIntegrations.map((integrations: any, index: number) => {
                      if (integrations.isActive &&
                        (!projectIntegrations.some((int: any) => int.isSelected) || integrations.isSelected)) {
                        return (
                          <div
                            className="bg-secondary-light w-48 max-h-12 rounded-md border p-10 flex justify-center items-center cursor-pointer hover:bg-gray-300 relative"
                            key={integrations.type}
                            onClick={() => handleIntegrationClick(integrations.type)}
                          >
                            <img
                              loading={"lazy"}
                              alt=""
                              src={`https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/${logoMap[integrations.type as keyof typeof logoMap]}.png`}
                            />
                            {(integrations.type === selectedPMtool || integrations.isSelected) &&
                                <HiCheckCircle className="text-green-600 absolute top-0 left-0" />}
                          </div>
                        );
                      }
                      return null;
                    }) : (
                      <div className="text-center dark:text-white text-gray-700 text-md font-normal font-['Inter']">
                        No PM tools are setup. Setup integrations on Org settings.
                      </div>
                    )
                  }
                </div>
                <div className="flex w-full h-12 items-center justify-end">
                  {projectIntegrations && projectIntegrations.length && projectIntegrations.filter((int: any) => int.isActive).length ? (
                    <CustomButton
                      className="text-white bg-primary-light dark:bg-primary-dark hover:bg-primary-dark"
                      onClick={handleSubmitSyncClick}
                      disabled={!selectedPMtool}
                    >
                      {!syncWithJiraLoading ? 'Sync' : "Syncing..."}
                    </CustomButton>
                  ) : null}
                </div>
                {selectedPMtool && (
                  <div className="mt-5">
                    <Label htmlFor="status_stages" className="flex mb-2">
                      {(selectedPMtool === 'jira' || selectedPMtool === 'asana') ?
                        'Swimlanes Configuration' :
                        'Project States Configuration'
                      }
                    </Label>
                    <Textarea
                      className="w-full rounded-none h-40 block p-2 text-gray-900 border border-gray-300 bg-tertiary-light sm:text-md focus:ring-primary-light focus:border-primary-light dark:bg-secondary-dark dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark"
                      id="status_stages"
                      name="status_stages"
                      placeholder={`{
                          "In QA": "25%",
                          "QA Tested": "50%",
                          "In Prod": "100%"
                        }`}
                      value={statusStages}
                      onChange={handleSwimlaneChange}
                    />
                    <div className="flex mt-4 gap-2 justify-end">
                      <Tooltip
                        content="Please sync with PM tool first"
                        placement="top"
                        trigger="hover"
                        animation="duration-300"
                        style="dark"
                        className="!z-[999]"
                      >
                        <div>
                          <CustomButton
                            onClick={handleSaveSwimLanes}
                            variant="primary"
                            size="sm"
                            disabled={!selectedPMtool || !selectedIntegration?.isSelected}
                          >
                            {(selectedPMtool === 'jira' || selectedPMtool === 'asana') ?
                              'Save Swimlanes' :
                              'Save Project States'
                            }
                          </CustomButton>
                        </div>
                      </Tooltip>
                      <CustomButton
                        onClick={() => {
                          setStatusStages('');
                        }}
                        variant="cancel"
                        size="sm"
                      >
                        Clear
                      </CustomButton>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Drawer>

      {/* Sync Tickets Modal */}
      <ConfirmModal
        showModal={showSyncTicketConfirm}
        setShowModal={handleSyncTicketConfirm}
        handleConfirm={submitSyncTicket}
        content={`Once synced, your choice of project management tool cannot be changed for this project. If sure, then click confirm.`}
        className={"!z-[99999999]"}
      />
    </>
  )
}

export default SyncModal;